// pages/app/Git.tsx — connect a GitHub/ADO repo; analyze YOUR commits/PRs (spec §6.3).
import { useState, type FormEvent } from 'react';
import { useServices } from '../../app/AppContext';
import { SourceList } from '../../components/SourceList';

export function Git() {
  const { api } = useServices();
  const [provider, setProvider] = useState('github');
  const [repoUrl, setRepoUrl] = useState('');
  const [token, setToken] = useState('');
  const [authorIdentity, setAuthorIdentity] = useState('');
  const [busy, setBusy] = useState(false);
  const [message, setMessage] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [reloadKey, setReloadKey] = useState(0);

  async function submit(e: FormEvent) {
    e.preventDefault();
    setBusy(true);
    setMessage(null);
    setError(null);
    try {
      await api.ingestGit({ provider, repoUrl, token, authorIdentity });
      setMessage('Repo connected — analyzing your commits and PRs.');
      setToken('');
      setRepoUrl('');
      setAuthorIdentity('');
      setReloadKey((k) => k + 1);
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to connect repo');
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="mx-auto max-w-5xl animate-fade-in space-y-6">
      <p className="text-sm text-fg-muted">
        We analyze only commits you authored and PRs you created — never anyone else's. Public repos
        need no token; add one only for private repos. Tokens are encrypted and stay server-side.
      </p>
      <form className="card space-y-5" onSubmit={submit}>
        <label className="block">
          <span className="field-label">Provider</span>
          <select
            className="select"
            value={provider}
            onChange={(e) => setProvider(e.target.value)}
            aria-label="Git provider"
          >
            <option value="github">GitHub</option>
            <option value="ado">Azure DevOps</option>
          </select>
        </label>
        <label className="block">
          <span className="field-label">Repository URL</span>
          <input
            className="input"
            value={repoUrl}
            onChange={(e) => setRepoUrl(e.target.value)}
            placeholder="https://github.com/you/repo"
            aria-label="Repository URL"
            required
          />
        </label>
        <label className="block">
          <span className="field-label">Access token</span>
          <input
            className="input"
            type="password"
            autoComplete="off"
            value={token}
            onChange={(e) => setToken(e.target.value)}
            placeholder="ghp_… / PAT (private repos only)"
            aria-label="Access token"
          />
          <span className="field-hint">
            Optional for public repos. Required for private repos. Stored encrypted.
          </span>
        </label>
        <label className="block">
          <span className="field-label">Your author identity</span>
          <input
            className="input"
            value={authorIdentity}
            onChange={(e) => setAuthorIdentity(e.target.value)}
            placeholder="you@example.com or username"
            aria-label="Author identity"
          />
          <span className="field-hint">
            Optional — for a public repo we default to the repo owner. Set it to scope analysis to
            your commits.
          </span>
        </label>

        {message ? (
          <p
            className="rounded-xl border border-success/20 bg-success/10 px-3.5 py-2.5 text-sm text-success"
            role="status"
          >
            {message}
          </p>
        ) : null}
        {error ? (
          <p
            className="rounded-xl border border-danger/20 bg-danger/10 px-3.5 py-2.5 text-sm text-danger"
            role="alert"
          >
            {error}
          </p>
        ) : null}

        <button type="submit" className="btn" disabled={busy || !repoUrl}>
          {busy ? 'Connecting…' : 'Connect & analyze'}
        </button>
      </form>

      <SourceList
        kind="git"
        title="Connected repositories"
        emptyText="No repositories connected yet."
        reloadKey={reloadKey}
        describe={(s) => (typeof s.ref === 'string' ? s.ref : null)}
        confirmMessage={(s) =>
          `Disconnect “${s.label ?? 'this repository'}”? Everything distilled from it will be removed.`
        }
      />
    </div>
  );
}
